import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiDollarSign, FiTrendingUp, FiCheckCircle, FiLayers } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import { WORK_ORDER_EXECUTION_DATA } from "./worksReportsData";

const WorkOrderExecutionProgressReport = () => {
  const gridRef = useRef(null);
  const [data] = useState(WORK_ORDER_EXECUTION_DATA);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const totalContractValue = data.reduce((s, r) => s + (r.contractValue || 0), 0);
    const totalCertifiedMB = data.reduce((s, r) => s + (r.certifiedWorkMB || 0), 0);
    const totalBilled = data.reduce((s, r) => s + (r.billedToDate || 0), 0);
    const totalUnbilled = data.reduce((s, r) => s + (r.unbilledProgress || 0), 0);

    return {
      totalContractValue,
      totalCertifiedMB,
      totalBilled,
      totalUnbilled,
    };
  }, [data]);

  const uniqueProjects = useMemo(() => {
    return Array.from(new Set(data.map((d) => d.project).filter(Boolean)));
  }, [data]);

  const getStatusChipType = (status) => {
    if (status === "Completed") {
      return "success";
    }
    if (status === "In Progress") {
      return "info";
    }
    if (status === "Punch List / Handover") {
      return "warning";
    }
    return "default";
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Work Order No.",
        field: "workOrderNo",
        colId: "workOrderNo",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              fontFamily: "monospace",
              color: "var(--color-primary, #0b6462)",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "Project & Site Location",
        field: "project",
        colId: "project",
        flex: 1.4,
        minWidth: 160,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</span>,
      },
      {
        headerName: "Subcontractor / Thekedar",
        field: "subcontractor",
        colId: "subcontractor",
        flex: 1.8,
        minWidth: 200,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</div>
            <small style={{ color: "#64748b" }}>{p.data?.scope}</small>
          </div>
        ),
      },
      {
        headerName: "Awarded Contract Value (PKR)",
        field: "contractValue",
        colId: "contractValue",
        flex: 1.6,
        minWidth: 170,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 700, color: "#0b6462" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Work Certified (MBs) (PKR)",
        field: "certifiedWorkMB",
        colId: "certifiedWorkMB",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#0284c7" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Total Billed to Date (PKR)",
        field: "billedToDate",
        colId: "billedToDate",
        flex: 1.5,
        minWidth: 160,
        cellRenderer: (p) => (
          <span style={{ fontWeight: 600, color: "#16a34a" }}>
            PKR {p.value ? Number(p.value).toLocaleString() : "0"}
          </span>
        ),
      },
      {
        headerName: "Financial %",
        field: "financialCompletion",
        colId: "financialCompletion",
        flex: 1.2,
        minWidth: 120,
        cellRenderer: (p) => {
          const val = p.value || 0;
          return <span style={{ fontWeight: 700, color: "#334155" }}>{val.toFixed(1)}%</span>;
        },
      },
      {
        headerName: "Physical %",
        field: "physicalCompletion",
        colId: "physicalCompletion",
        flex: 1.4,
        minWidth: 140,
        cellRenderer: (p) => {
          const val = p.value || 0;
          return (
            <div className="d-flex align-items-center" style={{ gap: "6px", width: "100%" }}>
              <div
                style={{
                  flexGrow: 1,
                  background: "#e2e8f0",
                  height: "7px",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${Math.min(val, 100)}%`,
                    background: val >= 100 ? "#16a34a" : "var(--color-primary, #0b6462)",
                    height: "100%",
                  }}
                />
              </div>
              <span style={{ fontWeight: 700, fontSize: "11px", minWidth: "35px" }}>
                {val.toFixed(0)}%
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Unbilled Progress (PKR)",
        field: "unbilledProgress",
        colId: "unbilledProgress",
        flex: 1.4,
        minWidth: 150,
        cellRenderer: (p) => {
          const val = p.value || 0;
          return (
            <span
              style={{
                fontWeight: 600,
                color: val > 0 ? "#d97706" : "#64748b",
              }}
            >
              PKR {Number(val).toLocaleString()}
            </span>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        colId: "status",
        flex: 1.3,
        minWidth: 140,
        cellRenderer: (p) => <StatusChip status={p.value} type={getStatusChipType(p.value)} />,
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.workOrderNo && row.workOrderNo.toLowerCase().includes(q)) ||
          (row.project && row.project.toLowerCase().includes(q)) ||
          (row.subcontractor && row.subcontractor.toLowerCase().includes(q)) ||
          (row.scope && row.scope.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.status !== statusFilter) {
        return false;
      }

      if (projectFilter !== "all" && row.project !== projectFilter) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, projectFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `Work_Order_Execution_Progress_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("Work Order progress report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL CONTRACT VALUE"
            value={`PKR ${(kpis.totalContractValue / 1000000).toFixed(2)}M`}
            subtitle={`PKR ${kpis.totalContractValue.toLocaleString()} awarded`}
            icon={FiDollarSign}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="WORK CERTIFIED (MB)"
            value={`PKR ${(kpis.totalCertifiedMB / 1000000).toFixed(2)}M`}
            subtitle="Verified physical site progress"
            icon={FiTrendingUp}
            color="#0284c7"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="BILLED TO DATE"
            value={`PKR ${(kpis.totalBilled / 1000000).toFixed(2)}M`}
            subtitle="Processed on running account bills"
            icon={FiCheckCircle}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="UNBILLED PROGRESS"
            value={`PKR ${(kpis.totalUnbilled / 1000000).toFixed(2)}M`}
            subtitle="Certified MB work pending billing"
            icon={FiLayers}
            color="#d97706"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="WO Progress Report"
            >
              WO Progress Report
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search WO, contractor, project..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Execution Status</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Punch List / Handover">Punch List / Handover</option>
                  <option value="Just Started">Just Started</option>
                </select>
                <label>Status</label>
              </div>
            </div>

            {/* Project Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <option value="all">All Projects</option>
                  {uniqueProjects.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <label>Project / Site</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderExecutionProgressReport;
